import { create } from 'zustand'

import type { TLocation } from '../../settings/api/types/editAccountSettings.types'
import type { GENDER } from '../../user/models/user.enum'

const WIZARD_STEPS = ['theme', 'topics', 'about-you', 'end'] as const
type TWizardStep = (typeof WIZARD_STEPS)[number]

interface IWizardState {
  currentStepName: TWizardStep
  totalSteps: number
  step: number
  tags: number[]
  gender: GENDER | undefined
  location: TLocation | undefined
  birthdate: Date | undefined
  countryCode: string | undefined
}

interface IWizardReducer {
  nextStep: VoidFunction
  prevStep: VoidFunction
  setStep: (step: TWizardStep) => void
  setTags: (tags: number[]) => void
  toggleTag: (tagId: number) => void
  setGender: (gender: GENDER) => void
  setBirthdate: (birthdate: Date | undefined) => void
  setLocation: (location: TLocation) => void
  isSelectedTag: (tagId: number) => boolean
  setUserInfos: (userInfo: Partial<Pick<IWizardState, 'countryCode' | 'gender' | 'birthdate' | 'location'>>) => void
  resetWizard: VoidFunction
}

const initialState: IWizardState = {
  currentStepName: 'theme',
  step: 1,
  totalSteps: WIZARD_STEPS.length,
  tags: [],
  gender: undefined,
  birthdate: undefined,
  location: undefined,
  countryCode: undefined,
}

export const useWizardStore = create<IWizardState & IWizardReducer>()((set, get) => ({
  ...initialState,
  setStep: (step) =>
    set((state) => ({
      ...state,
      currentStepName: step,
      step: WIZARD_STEPS.indexOf(step) + 1,
    })),
  nextStep: () =>
    set((state) => {
      const currentIndex = WIZARD_STEPS.indexOf(state.currentStepName)
      const nextIndex = Math.min(currentIndex + 1, WIZARD_STEPS.length - 1)
      return { currentStepName: WIZARD_STEPS[nextIndex], step: nextIndex + 1 }
    }),
  prevStep: () =>
    set((state) => {
      const currentIndex = WIZARD_STEPS.indexOf(state.currentStepName)
      const prevIndex = Math.max(currentIndex - 1, 0)
      return { currentStepName: WIZARD_STEPS[prevIndex], step: prevIndex + 1 }
    }),
  setTags: (tags) =>
    set((state) => ({
      ...state,
      tags,
    })),
  toggleTag: (tagId) =>
    set((state) => ({
      ...state,
      tags: state.tags.includes(tagId) ? state.tags.filter((tag) => tag !== tagId) : [...state.tags, tagId],
    })),
  setGender: (gender) =>
    set((state) => ({
      ...state,
      gender,
    })),
  setLocation: (location) =>
    set((state) => ({
      ...state,
      location,
    })),
  setBirthdate: (birthdate) =>
    set((state) => ({
      ...state,
      birthdate,
    })),
  isSelectedTag: (tagId) => get().tags.includes(tagId),
  setUserInfos: (userInfo) =>
    set((state) => ({
      ...state,
      ...userInfo,
    })),
  resetWizard: () => set(initialState),
}))
